import '../styles/ResultsFindingsWall.css';
import React, { useState, useEffect } from 'react';
import { useProjectCollection } from '../lib/projectData';

const FALLBACK_RUN_ID = 'run-2026-02-11-01';

function readSelectedRunId() {
  if (typeof window === 'undefined') return FALLBACK_RUN_ID;
  try {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    return id && id.trim() ? id.trim() : FALLBACK_RUN_ID;
  } catch (err) {
    return FALLBACK_RUN_ID;
  }
}

function formatStamp(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
}

function formatRunStamp(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function AuthorizedSticker() {
  return (
    <span className="rfw-sticker">
      <span className="rfw-sticker__dot" aria-hidden="true" />
      Authorized target only
    </span>
  );
}

function GhostWall() {
  return (
    <ul className="rfw-wall" aria-hidden="true">
      {[0, 1, 2, 3].map((n) => (
        <li
          key={`ghost-${n}`}
          className={`rfw-chip rfw-chip--ghost${n % 2 ? ' rfw-chip--pass' : ''}`}
          style={{ animationDelay: `${n * 90}ms` }}
        >
          <span className="rfw-ghost-line rfw-ghost-line--short" />
          <span className="rfw-ghost-line rfw-ghost-line--long" />
          <span className="rfw-ghost-line rfw-ghost-line--mono" />
        </li>
      ))}
    </ul>
  );
}

export default function ResultsFindingsWall() {
  const [selectedRunId] = useState(readSelectedRunId);
  const runsQ = useProjectCollection('test_run');
  const findingsQ = useProjectCollection('result_finding');
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    if (!runsQ.loading && !findingsQ.loading) setSettled(true);
  }, [runsQ.loading, findingsQ.loading]);

  const runs = Array.isArray(runsQ.data) ? runsQ.data : [];
  const findings = Array.isArray(findingsQ.data) ? findingsQ.data : [];
  const run = runs.find((r) => r && r.id === selectedRunId) || null;
  const loading = !settled && !runsQ.error && !findingsQ.error;
  const error = runsQ.error || findingsQ.error;

  const runFindings = run
    ? findings.filter((f) => f && f.runId === run.id)
    : findings.filter((f) => f && f.runId === selectedRunId);
  const failCount = runFindings.filter((f) => f.outcome === 'fail').length;
  const passCount = runFindings.filter((f) => f.outcome === 'pass').length;

  return (
    <section className="rfw-root" aria-labelledby="rfw-heading">
      <div className="rfw-rail" data-content-rail="workspace">
        <div className="rfw-panel" aria-busy={loading ? 'true' : 'false'}>
          <div className="rfw-eyebrow">
            <span className="rfw-eyebrow__dash" aria-hidden="true" />
            <h2 id="rfw-heading" className="rfw-eyebrow__text">Findings</h2>
            <AuthorizedSticker />
          </div>

          {/* ResultsRunMetaHeader */}
          <header className="rfw-meta">
            <div className="rfw-meta__lead">
              <p className="rfw-meta__name">{run ? run.name : 'Run not in local history'}</p>
              <dl className="rfw-meta__details">
                <div className="rfw-meta__item">
                  <dt>Target</dt>
                  <dd>{run ? run.target : selectedRunId}</dd>
                </div>
                {run && run.configurationSummary ? (
                  <div className="rfw-meta__item">
                    <dt>Config</dt>
                    <dd>{run.configurationSummary}</dd>
                  </div>
                ) : null}
                {run ? (
                  <div className="rfw-meta__item rfw-meta__item--mono">
                    <dt>Completed</dt>
                    <dd>{formatRunStamp(run.updatedAt || run.createdAt)}</dd>
                  </div>
                ) : null}
                {!loading && run ? (
                  <div className="rfw-meta__item">
                    <dt>Results</dt>
                    <dd>{`${failCount} fail · ${passCount} pass`}</dd>
                  </div>
                ) : null}
              </dl>
            </div>
            {run ? (
              <span className="rfw-meta__run-state" data-state={run.state}>{run.state}</span>
            ) : null}
          </header>

          {/* ResultsFindingsState → loading / empty / populated */}
          {loading ? (
            <>
              <p className="rfw-sr-only" role="status">Loading findings for this run</p>
              <GhostWall />
            </>
          ) : error ? (
            <div className="rfw-state rfw-state--error" role="alert">
              <h3 className="rfw-state__title">Findings could not be read</h3>
              <p className="rfw-state__body">
                {typeof error === 'string' ? error : (error && error.message) || 'The local findings store returned an error.'}
              </p>
            </div>
          ) : runFindings.length === 0 ? (
            <div className="rfw-state">
              <h3 className="rfw-state__title">No findings for this run</h3>
              <p className="rfw-state__body">
                This run has not recorded any credential findings yet. Open Run Monitor to follow its progress.
              </p>
            </div>
          ) : (
            <ul className="rfw-wall">
              {runFindings.map((f, i) => {
                const pass = f.outcome === 'pass';
                return (
                  <li
                    key={f.id}
                    className={`rfw-chip ${pass ? 'rfw-chip--pass' : 'rfw-chip--fail'}`}
                    style={{ animationDelay: `${Math.min(i, 11) * 70}ms` }}
                  >
                    <div className="rfw-chip__head">
                      <span className="rfw-chip__outcome">{f.outcome}</span>
                      <span className="rfw-chip__mark" aria-hidden="true">{pass ? '✓' : '!'}</span>
                    </div>
                    <h3 className="rfw-chip__target">{f.target}</h3>
                    <span className="rfw-chip__time">{formatStamp(f.timestamp)}</span>
                    <span className="rfw-chip__stamp">{pass ? 'Strong' : 'Weak password'}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}